import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api } from '../services/api';

export type AuthUser = {
  id: string;
  email: string;
  mobile: string;
  name: string;
  role: string;
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
};

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  login: (accessToken: string, refreshToken: string, user: AuthUser) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('odc_access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get<{ user: AuthUser }>('/auth/me');
      setUser(data.user);
    } catch {
      setUser(null);
      localStorage.removeItem('odc_access_token');
      localStorage.removeItem('odc_refresh_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const login = useCallback(
    (accessToken: string, refreshToken: string, u: AuthUser) => {
      localStorage.setItem('odc_access_token', accessToken);
      localStorage.setItem('odc_refresh_token', refreshToken);
      setUser(u);
    },
    []
  );

  const logout = useCallback(() => {
    localStorage.removeItem('odc_access_token');
    localStorage.removeItem('odc_refresh_token');
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshUser,
    }),
    [user, loading, login, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
